import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import Checkbox from '../ui/Checkbox'
import { MarketViewDiscipline, TechnologyNode } from '../../services/api'

// ── Helpers ────────────────────────────────────────────────────────

/** Collect all leaf IDs from a technology node. Leaves are nodes with no children. */
function collectLeafIds(node: TechnologyNode): number[] {
  if (!node.children || node.children.length === 0) {
    return [node.id]
  }
  const ids: number[] = []
  for (const child of node.children) {
    ids.push(...collectLeafIds(child))
  }
  return ids
}

/** Collect all leaf technology IDs in a discipline. */
function collectDisciplineLeafIds(discipline: MarketViewDiscipline): number[] {
  const ids: number[] = []
  for (const tech of discipline.technologies) {
    ids.push(...collectLeafIds(tech))
  }
  return ids
}

/** Check if any leaf under this node is in the selected set. */
function hasSelectedLeaf(node: TechnologyNode, selectedSet: Set<number>): boolean {
  if (!node.children || node.children.length === 0) {
    return selectedSet.has(node.id)
  }
  return node.children.some(child => hasSelectedLeaf(child, selectedSet))
}

/** Check if ALL leaf descendants are selected. */
function allLeavesSelected(node: TechnologyNode, selectedSet: Set<number>): boolean {
  if (!node.children || node.children.length === 0) {
    return selectedSet.has(node.id)
  }
  return node.children.every(child => allLeavesSelected(child, selectedSet))
}

/** Collect IDs of nodes that should be auto-expanded (have a selected descendant). */
function collectExpandedNodeIds(node: TechnologyNode, selectedSet: Set<number>): number[] {
  const ids: number[] = []
  if (node.children) {
    for (const child of node.children) {
      if (selectedSet.has(child.id) || hasSelectedLeaf(child, selectedSet)) {
        ids.push(node.id)
        ids.push(...collectExpandedNodeIds(child, selectedSet))
      }
    }
  }
  return ids
}

// ── Props ──────────────────────────────────────────────────────────

interface TechnologyTreeFilterProps {
  allDisciplines: MarketViewDiscipline[]
  selectedTechnologyIds: number[]
  onSelectionChange: (techIds: number[]) => void
}

// ── TechTreeNode ───────────────────────────────────────────────────

const TechTreeNode: React.FC<{
  node: TechnologyNode
  selectedSet: Set<number>
  onToggle: (nodeId: number, descendantIds: number[]) => void
  expandedNodes: Set<number>
  onToggleExpand: (nodeId: number) => void
  depth: number
}> = ({ node, selectedSet, onToggle, expandedNodes, onToggleExpand, depth }) => {
  const hasChildren = node.children && node.children.length > 0
  // For leaf nodes, check if in set. For parent nodes, checked = all leaves selected.
  const isSelected = hasChildren ? allLeavesSelected(node, selectedSet) : selectedSet.has(node.id)
  const isExpanded = expandedNodes.has(node.id)

  // Indeterminate: some (but not all) descendant leaves are selected
  const isIndeterminate = useMemo(() => {
    if (!hasChildren) return false
    const allLeaves = allLeavesSelected(node, selectedSet)
    const someDescendant = hasSelectedLeaf(node, selectedSet)
    return someDescendant && !allLeaves
  }, [node, selectedSet, hasChildren])

  const leafIds = useMemo(() => collectLeafIds(node), [node])

  return (
    <div>
      <div
        className="flex items-center gap-1.5 py-1 hover:bg-muted/30 cursor-pointer transition-colors"
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => onToggle(node.id, leafIds)}
      >
        {/* Expand/Collapse Button */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpand(node.id)
            }}
            className="p-0.5 hover:bg-muted rounded flex-shrink-0"
          >
            {isExpanded ? (
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
            )}
          </button>
        ) : (
          <span className="w-[18px] flex-shrink-0" />
        )}

        <Checkbox
          checked={isSelected && !isIndeterminate}
          indeterminate={isIndeterminate}
          onChange={() => {}} // handled by parent div onClick
          className="pointer-events-none"
        />

        <span className={`text-xs flex-1 truncate ${isSelected || isIndeterminate ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
          {node.name}
        </span>

        {node.product_count > 0 && (
          <span className="text-[10px] text-muted-foreground bg-muted px-1 py-0.5 rounded mr-2 flex-shrink-0">
            {node.product_count}
          </span>
        )}
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <TechTreeNode
              key={child.id}
              node={child}
              selectedSet={selectedSet}
              onToggle={onToggle}
              expandedNodes={expandedNodes}
              onToggleExpand={onToggleExpand}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main Component ─────────────────────────────────────────────────

const TechnologyTreeFilter: React.FC<TechnologyTreeFilterProps> = ({
  allDisciplines,
  selectedTechnologyIds,
  onSelectionChange,
}) => {
  const selectedSet = useMemo(() => new Set(selectedTechnologyIds), [selectedTechnologyIds])
  const [expandedDisciplines, setExpandedDisciplines] = useState<Set<number>>(new Set())
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set())

  // Auto-expand disciplines and nodes that contain selected technologies
  useEffect(() => {
    const discIds = new Set<number>()
    const nodeIds = new Set<number>()

    for (const disc of allDisciplines) {
      const discTechIds = collectDisciplineLeafIds(disc)
      const hasSelection = discTechIds.some(id => selectedSet.has(id))
      if (hasSelection) {
        discIds.add(disc.id)
        // Auto-expand tree nodes to show selected techs
        for (const tech of disc.technologies) {
          for (const id of collectExpandedNodeIds(tech, selectedSet)) {
            nodeIds.add(id)
          }
        }
      }
    }

    setExpandedDisciplines(prev => {
      // Merge new expansions with existing (don't collapse what user expanded)
      const merged = new Set(prev)
      discIds.forEach(id => merged.add(id))
      return merged
    })
    setExpandedNodes(prev => {
      const merged = new Set(prev)
      nodeIds.forEach(id => merged.add(id))
      return merged
    })
  }, [allDisciplines, selectedSet])

  const toggleDisciplineExpand = useCallback((disciplineId: number) => {
    setExpandedDisciplines(prev => {
      const next = new Set(prev)
      if (next.has(disciplineId)) next.delete(disciplineId)
      else next.add(disciplineId)
      return next
    })
  }, [])

  const toggleNodeExpand = useCallback((nodeId: number) => {
    setExpandedNodes(prev => {
      const next = new Set(prev)
      if (next.has(nodeId)) next.delete(nodeId)
      else next.add(nodeId)
      return next
    })
  }, [])

  // Toggle a node: if checked → uncheck all leaves; if unchecked → check all leaves
  const handleToggle = useCallback((_nodeId: number, leafIds: number[]) => {
    // "Checked" means all leaves under this node are selected
    const allChecked = leafIds.every(id => selectedSet.has(id))

    if (allChecked) {
      // Uncheck: remove all leaves
      const toRemove = new Set(leafIds)
      onSelectionChange(selectedTechnologyIds.filter(id => !toRemove.has(id)))
    } else {
      // Check: add all leaves
      const current = new Set(selectedTechnologyIds)
      for (const id of leafIds) current.add(id)
      onSelectionChange(Array.from(current))
    }
  }, [selectedSet, selectedTechnologyIds, onSelectionChange])

  // Discipline-level toggle: check/uncheck all technologies in the discipline
  const handleDisciplineToggle = useCallback((discipline: MarketViewDiscipline) => {
    const allTechIds = collectDisciplineLeafIds(discipline)
    const allSelected = allTechIds.every(id => selectedSet.has(id))

    if (allSelected) {
      // Uncheck all
      const toRemove = new Set(allTechIds)
      onSelectionChange(selectedTechnologyIds.filter(id => !toRemove.has(id)))
    } else {
      // Check all
      const current = new Set(selectedTechnologyIds)
      for (const id of allTechIds) current.add(id)
      onSelectionChange(Array.from(current))
    }
  }, [selectedSet, selectedTechnologyIds, onSelectionChange])

  if (allDisciplines.length === 0) {
    return null
  }

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="px-3 py-2 border-b border-border">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Technologies
        </h3>
      </div>

      {/* Technology Tree */}
      <div className="max-h-[400px] overflow-y-auto">
        {allDisciplines.map((discipline) => {
          const isExpanded = expandedDisciplines.has(discipline.id)
          const allTechIds = collectDisciplineLeafIds(discipline)
          const selectedCount = allTechIds.filter(id => selectedSet.has(id)).length
          const allSelected = selectedCount === allTechIds.length && allTechIds.length > 0
          const someSelected = selectedCount > 0 && !allSelected

          return (
            <div key={discipline.id} className="border-b border-border last:border-b-0">
              {/* Discipline Header */}
              <div
                className="flex items-center gap-1.5 px-3 py-1.5 hover:bg-muted/30 transition-colors cursor-pointer bg-muted/10"
                onClick={() => handleDisciplineToggle(discipline)}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleDisciplineExpand(discipline.id)
                  }}
                  className="p-0.5 hover:bg-muted rounded flex-shrink-0"
                >
                  {isExpanded ? (
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                  )}
                </button>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={() => {}} // handled by parent div onClick
                  className="pointer-events-none"
                />
                <span className={`text-xs font-medium flex-1 ${allSelected || someSelected ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {discipline.name}
                </span>
                {selectedCount > 0 && (
                  <span className="text-[10px] text-muted-foreground">
                    {selectedCount}/{allTechIds.length}
                  </span>
                )}
              </div>

              {/* Technologies Tree */}
              {isExpanded && discipline.technologies && (
                <div className="pb-1">
                  {discipline.technologies.map((tech) => (
                    <TechTreeNode
                      key={tech.id}
                      node={tech}
                      selectedSet={selectedSet}
                      onToggle={handleToggle}
                      expandedNodes={expandedNodes}
                      onToggleExpand={toggleNodeExpand}
                      depth={1}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default TechnologyTreeFilter
